import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import OfferFreecopyTable from "./OfferFreecopyTable";
import { OFFER_PLAN_TYPES } from "./offerPlan.constants";
import { offerViewColumns, offerViewRow } from "./tableParams/offerViewColumns";
import StatsAPI from "../../api/StatsAPI";
import ExportCsvButton from "../analytics/charts/ExportCsvButton";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import { userEditPath } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { OfferViewRow } from "./tableParams/offerViewColumns.types";
import type { OfferPlanTypeValue } from "../../models/OfferPlan.types";

const { SVOD, TVOD } = OFFER_PLAN_TYPES;

function OfferView(): JSX.Element {
  const { t } = useTranslation("editor-offers");
  const { projectSlug, id: offerId } = useParams<{
    projectSlug: string;
    id: string;
  }>();

  const getSVODOfferUserStatsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getOfferUserStats,
    params: [{ offerPlanType: SVOD, offerId, projectSlug }],
  });
  const getTVODOfferUserStatsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getOfferUserStats,
    params: [{ offerPlanType: TVOD, offerId, projectSlug }],
  });

  const {
    data: statsSVOD,
    isLoading: isSVODLoading,
    isError: isSVODError,
    refetch: refetchSVOD,
  } = useQuery({ ...getSVODOfferUserStatsQuery, enabled: !!offerId });
  const {
    data: statsTVOD,
    isLoading: isTVODLoading,
    isError: isTVODError,
    refetch: refetchTVOD,
  } = useQuery({ ...getTVODOfferUserStatsQuery, enabled: !!offerId });

  const svodRowList = useMemo(
    (): OfferViewRow[] =>
      (statsSVOD?.data ?? []).map((plan) => offerViewRow(plan, t)),
    [statsSVOD?.data, t],
  );
  const tvodRowList = useMemo(
    (): OfferViewRow[] =>
      (statsTVOD?.data ?? []).map((plan) => offerViewRow(plan, t)),
    [statsTVOD?.data, t],
  );

  const svodColumnList = useMemo(
    () => offerViewColumns({ t, projectSlug, offerPlanType: SVOD }),
    [t, projectSlug],
  );
  const tvodColumnList = useMemo(
    () => offerViewColumns({ t, projectSlug, offerPlanType: TVOD }),
    [t, projectSlug],
  );

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- ?? would keep an empty label */
  const offerLabel =
    statsTVOD?.label || statsSVOD?.label || t("editor-menu:offers");
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const breadcrumbList = [
    {
      name: t("editor-menu:offerManagement"),
      url: "/offers",
    },
    {
      name: offerLabel,
    },
  ];

  const getRowHref = (row: OfferViewRow): string | undefined =>
    userEditPath(projectSlug, row.user_uuid);

  // Tout le tableau, côté serveur : sans la recherche ni les filtres.
  const exportButton = (
    offerPlanType: OfferPlanTypeValue,
    isLoading: boolean,
    rowCount: number,
  ): JSX.Element => {
    const handleFetchCsv = async (): Promise<string> =>
      StatsAPI.getOfferUserStats({
        offerId,
        offerPlanType,
        isExport: true,
        projectSlug,
      });

    return (
      <ExportCsvButton
        disabled={isLoading || rowCount === 0}
        fetchCsv={handleFetchCsv}
        fileName={`offer_user_${offerPlanType}.csv`}
      />
    );
  };

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbList} />
      <div className="flex flex-col gap-6">
        <DataTable
          columns={svodColumnList}
          data={svodRowList}
          getRowHref={getRowHref}
          headerActions={exportButton(SVOD, isSVODLoading, svodRowList.length)}
          isError={isSVODError}
          isLoading={isSVODLoading}
          onRetry={refetchSVOD}
          title={t(`editor-analytics:users_title_${SVOD}`)}
          urlSync={{ prefix: "svod" }}
        />
        <DataTable
          columns={tvodColumnList}
          data={tvodRowList}
          getRowHref={getRowHref}
          headerActions={exportButton(TVOD, isTVODLoading, tvodRowList.length)}
          isError={isTVODError}
          isLoading={isTVODLoading}
          onRetry={refetchTVOD}
          title={t(`editor-analytics:users_title_${TVOD}`)}
          urlSync={{ prefix: "tvod" }}
        />
        <OfferFreecopyTable offerUuid={offerId} />
      </div>
    </CappedPage>
  );
}

export default OfferView;
